import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, ArrowRight, Briefcase } from 'lucide-react';
import { HERO_IMAGE } from '@/data/site';
import { SlashMark } from './AmdenMark';
import { scrollToId } from '@/lib/scroll';

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, reduce }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={reduce ? false : { y: '110%' }}
      animate={reduce ? undefined : { y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 56]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -36]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative overflow-hidden bg-white"
      aria-labelledby="hero-heading"
    >
      <SlashMark
        className="pointer-events-none absolute -right-16 top-10 h-[420px] w-[420px] opacity-[0.05] sm:-right-8 lg:right-24"
        bar="#0B1729"
        dot="#FFB800"
      />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-gold-100/50 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-20 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:items-center lg:gap-10 lg:px-8 lg:pb-28 lg:pt-24">
        <div className="lg:col-span-6">
          <motion.p
            data-testid="hero-eyebrow"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-gold-700"
          >
            <span className="h-px w-8 bg-gold-600" aria-hidden="true" />
            Advice that guides. Solutions that deliver.
          </motion.p>

          <h1
            id="hero-heading"
            data-testid="hero-heading"
            className="mt-6 text-5xl font-extrabold leading-[1.04] tracking-tight text-navy-900 sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.1} reduce={reduce}>
              Award-Winning
            </MaskedLine>
            <MaskedLine delay={0.22} reduce={reduce}>
              <span className="font-serif font-semibold italic text-gold-700">Business Consultancy</span>
            </MaskedLine>
          </h1>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: EASE }}
          >
            <p data-testid="hero-subheading" className="mt-6 text-lg font-bold text-navy-800 sm:text-xl">
              Practical Business Solutions.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-intro">
              At Amden Group, we provide consultancy, systems and practical support to help
              organisations improve their operations, manage and develop their people, drive growth
              and bring new ideas to life.
            </p>
            <p className="mt-4 max-w-xl text-base font-semibold tracking-wide sm:text-lg" data-testid="hero-divisions">
              <span className="text-gold-700">Operations.</span>{' '}
              <span className="text-service-people">People.</span>{' '}
              <span className="text-service-growth">Growth.</span>{' '}
              <span className="text-service-launch">Launch.</span>
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              From strategic advice to practical implementation, we provide the expertise, systems
              and support to make it happen.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => scrollToId('#solution-finder')}
                data-testid="hero-get-started-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-4 text-base font-bold text-white transition-colors hover:bg-navy-700"
              >
                Get Started
                <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
              </button>
              <Link
                to="/contact#book-appointment"
                data-testid="hero-speak-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-md border-2 border-navy-900 px-7 py-[14px] text-base font-bold text-navy-900 transition-colors hover:bg-navy-900 hover:text-white"
              >
                Speak to Us
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="relative lg:col-span-6">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96 }}
            animate={reduce ? undefined : { opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-xl border border-slate-200 shadow-lift">
              <motion.img
                src={HERO_IMAGE.src}
                alt={HERO_IMAGE.alt}
                width="1200"
                height="900"
                fetchPriority="high"
                data-testid="hero-image"
                style={{ y: imageY }}
                className="aspect-[4/3] w-full scale-[1.12] object-cover"
              />
            </div>

            <motion.div
              data-testid="hero-stat-card"
              initial={reduce ? false : { opacity: 0, y: 24 }}
              animate={reduce ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
              style={{ y: cardY }}
              className="absolute -bottom-8 left-4 flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-lift sm:left-8"
            >
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gold-100 text-gold-700">
                <Briefcase className="h-5 w-5" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Client Projects Delivered
                </span>
                <span className="mt-0.5 block text-3xl font-extrabold tracking-tight text-navy-900">
                  150+
                </span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
